// The words behind the footer: about, contact, and the three legal documents.
// Bodies, not screens. Each had a solo page of its own until 2 Aug 2026, when
// the routes became doors that open the modal over whatever the reader is
// already on (`landingFor` in the store). One copy of each text, one place it
// renders, nothing to drift.
import { type LegalDocument } from "../../shared/legal.js";
import {
  ABOUT_PARAS,
  CONTACT_PARAS,
  splitPara,
  type CompanyPara as CompanyParaData,
} from "../../shared/company.js";
import { docPath, useStore, type CompanyPageId } from "../state/store.js";

/* One paragraph of company copy, with its link put back. The words come from
   `shared/company.ts` so the server-rendered landing pages say the same
   thing; what is decided here is only what a link does in an app that has a
   modal layer. */
function CompanyPara({ para }: { para: CompanyParaData }) {
  const openSheet = useStore((s) => s.openSheet);
  const { before, linked, after } = splitPara(para);
  if (!linked || !para.link) return <p>{para.text}</p>;
  const link = para.link;
  // `.nobreak` on the tail so the full stop after a link never starts its own
  // line: a <button> stays an atomic inline box whatever the stylesheet asks.
  const tail = <span className="nobreak">{after}</span>;
  if (link.sheet) {
    return (
      <p>
        {before}
        <span className="nobreak">
          <button className="credlink" onClick={() => openSheet({ kind: link.sheet as "photo-credits" })}>
            {linked}
          </button>
          {after}
        </span>
      </p>
    );
  }
  if (link.doc) {
    const doc = link.doc;
    return (
      <p>
        {before}
        <a
          className="credlink"
          href={docPath(doc)}
          onClick={(e) => {
            e.preventDefault();
            openSheet({ kind: "company", page: doc });
          }}
        >
          {linked}
        </a>
        {tail}
      </p>
    );
  }
  return (
    <p>
      {before}
      <a className="credlink" href={link.href}>
        {linked}
      </a>
      {tail}
    </p>
  );
}

export function AboutBody() {
  return (
    <>
      {ABOUT_PARAS.map((p, i) => (
        <CompanyPara key={i} para={p} />
      ))}
    </>
  );
}

export function ContactBody() {
  return (
    <>
      {CONTACT_PARAS.map((p, i) => (
        <CompanyPara key={i} para={p} />
      ))}
    </>
  );
}

export const COMPANY_PAGES: Record<CompanyPageId, { title: string; Body: () => React.ReactElement }> = {
  about: { title: "About Practice", Body: AboutBody },
  contact: { title: "Contact", Body: ContactBody },
};

/* The legal documents render from `shared/legal.ts` rather than from JSX
   written here (1 Aug 2026): counsel's text is the source of record, and each
   document carries a version the consent record has to name. */
export function LegalBody({ doc }: { doc: LegalDocument }) {
  return (
    <>
      {/* One date, and only when there is one (revised 1 Aug 2026, Rob). It
          said "Effective on launch · Version 2026-08-01", which was the same
          fact twice: a version formatted as a date reads as a second
          effective date. The version is plumbing for the consent record,
          naming which text a reader accepted, and never ships on the page. A
          draft notice sat here too and came out the same day: the document
          reads as what it is, and a banner explaining that it is not live
          yet was us talking about ourselves on a page about their rights. */}
      <p className="u" style={{ marginBottom: 10 }}>
        Effective {doc.effective}
      </p>
      {doc.blocks.map((b, i) => {
        if ("h" in b) return <h3 key={i} className="legalh">{b.h}</h3>;
        if ("h3" in b) return <h4 key={i} className="legalh4">{b.h3}</h4>;
        if ("ul" in b)
          return (
            <ul key={i} className="legalul">
              {b.ul.map((li, j) => (
                <li key={j}>{li}</li>
              ))}
            </ul>
          );
        if ("address" in b)
          return (
            <p key={i} className="legaladdr">
              {b.address.map((line, j) => (
                <span key={j}>{line}</span>
              ))}
            </p>
          );
        return <p key={i}>{b.p}</p>;
      })}
    </>
  );
}

/* The addresses survive the move to modals: /privacy, /terms and /health-data
   still resolve, they just open the document over the screen the reader
   belongs on rather than replacing it. A policy needs a stable address for
   the places a modal cannot reach, an email footer or a store listing among
   them; it does not need a page of its own. `landingFor` in the store does
   the mapping, and `LegalSheet` reads the text straight out of
   `shared/legal.ts`. */
